import { SuccessResponseList } from './common'
import { User } from './user'

/**
 * Top-level audit category (what kind of entity the log belongs to)
 */
export enum AuditType {
  DOCUMENT_VIEWED = 'DOCUMENT_VIEWED',
  HISTORY_LOG = 'HISTORY_LOG',
}

/**
 * Fine-grained log type for company-level events
 */
export enum CompanyLogType {
  COMPANY_CREATED = 'COMPANY_CREATED',
  COMPANY_UPDATED = 'COMPANY_UPDATED',
  COMPANY_DELETED = 'COMPANY_DELETED',
}

/**
 * Fine-grained log type for project-level events
 */
export enum ProjectLogType {
  PROJECT_CREATED = 'PROJECT_CREATED',
  PROJECT_UPDATED = 'PROJECT_UPDATED',
  PROJECT_DELETED = 'PROJECT_DELETED',
}

/**
 * Fine-grained log type for deliverable set events
 */
export enum DeliverableSetLogType {
  DELIVERABLE_SET_CREATED = 'DELIVERABLE_SET_CREATED',
  DELIVERABLE_SET_UPDATED = 'DELIVERABLE_SET_UPDATED',
  DELIVERABLE_SET_DELETED = 'DELIVERABLE_SET_DELETED',
}

/**
 * Time-frame preset values for the global history filter.
 * For "all time" simply omit time_frame from the request body.
 */
export enum AuditTimeFrame {
  THIS_WEEK = 'THIS_WEEK',
  THIS_MONTH = 'THIS_MONTH',
  THIS_QUARTER = 'THIS_QUARTER',
  CUSTOM = 'CUSTOM',
}

export interface RequestAuditLog {
  audit_type: AuditType
  timestamp?: string
  limit?: number
  offset?: number
  sort_by?: string
  filter_by?: {
    document_id?: string[]
    document_version_id?: string[]
    company_id?: string[]
    project_id?: string[]
    deliverable_set_id?: string[]
    user_id?: string[]
    created_at?: string[]
    log_type?: string[]
    /** Preset time-frame shortcut */
    time_frame?: AuditTimeFrame
    /** ISO date string — used when time_frame === CUSTOM */
    start_date?: string
    /** ISO date string — used when time_frame === CUSTOM */
    end_date?: string
  }
}

/**
 * Legacy alias: DOCUMENT_VIEWED audit returns a flat list of User objects.
 * Kept as `= User` for backward compatibility with ViewerTracker.tsx.
 */
export type AuditLogEntry = User

export interface SuccessResponseListAuditLogEntry extends SuccessResponseList<AuditLogEntry> {}

// ---------------------------------------------------------------------------
// Entity history audit (audit_type: HISTORY_LOG)
// Used for Company, Project, Deliverable Set, and future entity types.

/** What changed in the event */
export interface EntityAuditLogMetadata {
  type_entity: string // 'company' | 'project' | 'deliverable_set' | 'thread' | 'threadcomment'
  entity_id: string
  field?: string | null
  old_value?: unknown
  new_value?: unknown
  /** Extra context like comment content and document name */
  extra?: {
    content?: string
    document_name?: string
    [key: string]: unknown
  }
}

/** Minimal entity reference for Company and Project (id + name) */
export interface CompanyRef {
  id: string
  name: string
}

/** Deliverable Set ref — backend uses `deliverable_set_name` instead of `name` */
export interface DeliverableSetRef {
  id: string
  deliverable_set_name: string
}

export interface ProjectRef {
  id: string
  project_name?: string
}

/**
 * Fine-grained log type for Document (TFL file) events
 */
export enum DocumentLogType {
  DOCUMENT_CREATED = 'DOCUMENT_CREATED',
  DOCUMENT_REVISIONED = 'DOCUMENT_REVISIONED',
  DOCUMENT_UPDATED = 'DOCUMENT_UPDATED',
  DOCUMENT_DELETED = 'DOCUMENT_DELETED',
  DOCUMENT_APPROVED = 'DOCUMENT_APPROVED',
  DOCUMENT_REJECTED = 'DOCUMENT_REJECTED',
}

/**
 * Fine-grained log type for approval workflow events
 */
export enum ApprovalLogType {
  APPROVAL_REQUEST_SENT = 'DELIVERABLE_SET_APPROVAL_REQUESTED',
  APPROVAL_REQUEST_CANCELLED = 'DELIVERABLE_SET_APPROVAL_CANCELLED',
  DELIVERABLE_SET_DELEGATED = 'DELIVERABLE_SET_APPROVAL_DELEGATED',
}

/**
 * Fine-grained log type for comment events
 */
export enum CommentLogType {
  COMMENT_CREATED = 'COMMENT_CREATED',
  COMMENT_UPDATED = 'COMMENT_UPDATED',
  COMMENT_DELETED = 'COMMENT_DELETED',
  COMMENT_ACTIONABLE = 'COMMENT_MARKED_ACTIONABLE',
  COMMENT_NOT_ACTIONABLE = 'COMMENT_MARKED_NOT_ACTIONABLE',
}

/**
 * Fine-grained log type for reply events
 */
export enum ReplyLogType {
  REPLY_CREATED = 'REPLY_CREATED',
  REPLY_UPDATED = 'REPLY_UPDATED',
  REPLY_DELETED = 'REPLY_DELETED',
}

/**
 * Full audit entry for entity history drawers (Company, Project, etc.).
 * Backend returns entity-specific refs (company/project) at the top level
 * depending on the audit_type.
 */
export interface EntityAuditLogEntry {
  id: string
  audit_type: string
  user_id: string
  user: User
  created_at: string
  log_type: string
  log_metadata?: EntityAuditLogMetadata

  company_id?: string
  company?: CompanyRef

  project_id?: string
  project?: ProjectRef

  deliverable_set_id?: string
  deliverable_set?: DeliverableSetRef

  tfl_file_id?: string
  tfl_file?: CompanyRef

  comment_id?: string
  reply_id?: string
  document_id?: string
  document_version_id?: string
  delegate_to?: Pick<User, 'id' | 'first_name' | 'last_name'>
}

export interface SuccessResponseListEntityAuditLogEntry extends SuccessResponseList<EntityAuditLogEntry> {}
